<template>
  <section class="expenses-toolbar app-filter-toolbar">
    <div class="toolbar-main-row app-filter-toolbar-row">
      <div class="search-wrap app-filter-search-wrap">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input
          v-model="search"
          type="text"
          :placeholder="searchPlaceholder"
          @input="emitSearch"
          @keyup.enter="emitApply"
        />
      </div>

      <div class="toolbar-right app-filter-toolbar-right">
        <AppFilterDateRange
          v-model="dateRangeValue"
          class="app-filter-date-inline"
          label=""
          :show-input="showDateInput"
          :presets="dateRangePresets"
          :initial-preset-key="initialPresetKey"
          @change="onDateRangeChange"
        />

        <multiselect
          v-model="filters[inlineFilterKey]"
          :options="options[inlineFilterKey]"
          track-by="value"
          label="label"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :searchable="inlineFilterConfig?.searchable"
          :allow-empty="true"
          :max-height="220"
          :placeholder="inlineFilterConfig?.placeholder"
          class="expenses-toolbar-provider-inline app-filter-inline-select app-filter-select app-select app-select--filters"
          @select="onSelect(inlineFilterKey, $event)"
          @remove="onRemove(inlineFilterKey)"
          @close="flushFiltersEmit"
        >
          <template #selection="{ values, isOpen }">
            <span v-if="!isOpen" class="multiselect__single">
              {{ selectionLabel(inlineFilterKey, values) }}
            </span>
          </template>
          <template #noResult>
            <span>{{ emptyOptionText }}</span>
          </template>
          <template #noOptions>
            <span>{{ emptyOptionText }}</span>
          </template>
        </multiselect>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn"
          :class="{ active: showFilters }"
          :title="filtersButtonTitle"
          @click="toggleFilters"
        >
          <i class="fa fa-filter" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn sales-toolbar-search-btn"
          :title="searchButtonTitle"
          @click="emitApply"
        >
          <i class="fa fa-search" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <transition
      name="filters-expand"
      @after-leave="handleFiltersAfterLeave"
    >
      <div v-show="showFilters" class="filters-panel app-filter-panel">
        <div class="expenses-filters-grid expenses-filters-grid--first-row" :style="firstRowGridStyle">
          <div
            v-for="filter in firstRowFilterConfigs"
            :key="filter.key"
            class="filter-field app-filter-field"
          >
            <span>{{ filter.label }}</span>
            <multiselect
              v-model="filters[filter.key]"
              :options="options[filter.key]"
              track-by="value"
              label="label"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              :searchable="filter.searchable"
              :allow-empty="true"
              :max-height="220"
              :placeholder="filter.placeholder"
              class="app-filter-select app-select app-select--filters"
              @select="onSelect(filter.key, $event)"
              @remove="onRemove(filter.key)"
              @close="flushFiltersEmit"
            >
              <template #selection="{ values, isOpen }">
                <span v-if="!isOpen" class="multiselect__single">
                  {{ selectionLabel(filter.key, values) }}
                </span>
              </template>
              <template #noResult>
                <span>{{ emptyOptionText }}</span>
              </template>
              <template #noOptions>
                <span>{{ emptyOptionText }}</span>
              </template>
            </multiselect>
          </div>
        </div>

        <div class="expenses-filters-grid expenses-filters-grid--second-row" :style="secondRowGridStyle">
          <div
            v-for="filter in secondRowFilterConfigs"
            :key="filter.key"
            class="filter-field app-filter-field"
          >
            <span>{{ filter.label }}</span>
            <multiselect
              v-model="filters[filter.key]"
              :options="options[filter.key]"
              track-by="value"
              label="label"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              :searchable="filter.searchable"
              :allow-empty="true"
              :max-height="220"
              :placeholder="filter.placeholder"
              class="app-filter-select app-select app-select--filters"
              @select="onSelect(filter.key, $event)"
              @remove="onRemove(filter.key)"
              @close="flushFiltersEmit"
            >
              <template #selection="{ values, isOpen }">
                <span v-if="!isOpen" class="multiselect__single">
                  {{ selectionLabel(filter.key, values) }}
                </span>
              </template>
              <template #noResult>
                <span>{{ emptyOptionText }}</span>
              </template>
              <template #noOptions>
                <span>{{ emptyOptionText }}</span>
              </template>
            </multiselect>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import {
  ALL_OPTION_VALUE,
  applyRemoveOption,
  applySelectOption,
  buildSelectedValues,
  ensureDefaultSelection,
} from '@/utils/filterSelectors';
import AppFilterDateRange from '@/components/common/AppFilterDateRange.vue';
import {
  buildToolbarApplyPayload,
  buildToolbarSelectionLabel,
  createToolbarAllOption,
  extractToolbarDateRange,
  mapToolbarOptions,
  normalizeToolbarDate,
  resolveToolbarToggleState,
  shouldEmitToolbarDateChange,
} from '@/composables/shared/useFilterToolbarShared';

export default {
  name: 'ExpenseFeeToolbar',
  components: {
    AppFilterDateRange,
  },
  props: {
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    searchPlaceholder: {
      type: String,
      required: true,
    },
    emptyOptionText: {
      type: String,
      required: true,
    },
    filtersButtonTitle: {
      type: String,
      required: true,
    },
    searchButtonTitle: {
      type: String,
      default: 'Buscar',
    },
    dateRangePresets: {
      type: Array,
      required: true,
    },
    initialPresetKey: {
      type: String,
      default: 'this_year',
    },
    inlineFilterKey: {
      type: String,
      required: true,
    },
    firstRowKeys: {
      type: Array,
      default: () => [],
    },
    secondRowKeys: {
      type: Array,
      default: () => [],
    },
    filterConfigs: {
      type: Array,
      default: () => [],
    },
    allOptionLabels: {
      type: Object,
      default: () => ({}),
    },
    selectionAllLabels: {
      type: Object,
      default: () => ({}),
    },
    firstRowColumns: {
      type: Number,
      default: 0,
    },
    secondRowColumns: {
      type: Number,
      default: 0,
    },
  },
  emits: ['search-change', 'filters-change', 'apply'],
  data() {
    const options = {};
    const filters = {};

    this.filterConfigs.forEach((config) => {
      const allOption = this.createAllOptionForKey(config.key);
      options[config.key] = [allOption];
      filters[config.key] = [allOption];
    });

    return {
      search: '',
      showFilters: false,
      showDateInput: false,
      dateRangeValue: null,
      options,
      filters,
    };
  },
  computed: {
    inlineFilterConfig() {
      return this.filterConfigByKey(this.inlineFilterKey);
    },
    firstRowFilterConfigs() {
      return this.firstRowKeys
        .map((key) => this.filterConfigByKey(key))
        .filter(Boolean);
    },
    secondRowFilterConfigs() {
      return this.secondRowKeys
        .map((key) => this.filterConfigByKey(key))
        .filter(Boolean);
    },
    firstRowGridStyle() {
      const columns = this.firstRowColumns > 0 ? this.firstRowColumns : this.firstRowFilterConfigs.length;
      return { '--toolbar-grid-columns': columns || 1 };
    },
    secondRowGridStyle() {
      const columns = this.secondRowColumns > 0 ? this.secondRowColumns : this.secondRowFilterConfigs.length;
      return { '--toolbar-grid-columns': columns || 1 };
    },
  },
  watch: {
    filterOptions: {
      deep: true,
      immediate: true,
      handler() {
        this.syncOptionsFromProps();
      },
    },
  },
  beforeUnmount() {
    // no-op
  },
  methods: {
    createAllOptionForKey(key) {
      return createToolbarAllOption(this.allOptionLabels, key);
    },
    toggleFilters() {
      const nextState = resolveToolbarToggleState(this.showFilters);
      this.showFilters = nextState.showFilters;

      if (nextState.showDateInput !== null) {
        this.showDateInput = nextState.showDateInput;
      }
    },
    handleFiltersAfterLeave() {
      this.showDateInput = false;
    },
    emitSearch() {
      this.$emit('search-change', this.search);
    },
    flushFiltersEmit(trigger = 'manual') {
      this.emitFilters(trigger);
    },
    normalizeDate(value) {
      return normalizeToolbarDate(value);
    },
    onDateRangeChange(payload) {
      if (shouldEmitToolbarDateChange(payload, this.dateRangeValue)) {
        this.flushFiltersEmit('date');
      }
    },
    extractDateRange() {
      return extractToolbarDateRange(this.dateRangeValue, {
        startKey: 'start',
        endKey: 'end',
      });
    },
    selectionLabel(key, values) {
      const selected = Array.isArray(values) ? values : [];
      const hasAllSelected = selected.length === 0 || selected.some((option) => option?.value === ALL_OPTION_VALUE);

      if (hasAllSelected) {
        const customLabel = String(this.selectionAllLabels?.[key] || '').trim();
        return buildToolbarSelectionLabel(values, customLabel);
      }

      return buildToolbarSelectionLabel(values);
    },
    selectedValues(key) {
      return buildSelectedValues(this.filters[key]);
    },
    onSelect(key, option) {
      this.filters[key] = applySelectOption(this.filters[key], option, this.options[key]?.[0]);
      this.emitFilters('filters');
    },
    onRemove(key) {
      this.filters[key] = applyRemoveOption(this.filters[key], this.options[key]?.[0]);
      this.emitFilters('filters');
    },
    ensureAllDefault(key) {
      this.filters[key] = ensureDefaultSelection(this.filters[key], this.options[key]?.[0]);
    },
    filterConfigByKey(key) {
      return this.filterConfigs.find((filter) => filter.key === key) || null;
    },
    mapToOptionList(items) {
      return mapToolbarOptions(items);
    },
    syncOptionsFromProps() {
      const source = this.filterOptions || {};

      this.filterConfigs.forEach((config) => {
        const key = config.key;
        const allOption = this.createAllOptionForKey(key);
        this.options[key] = [allOption, ...this.mapToOptionList(source[key])];
        this.ensureAllDefault(key);
      });
    },
    emitFilters(trigger = 'manual') {
      const dateRange = this.extractDateRange();
      const payload = {
        search: this.search,
        date_start: dateRange.start,
        date_end: dateRange.end,
      };

      this.filterConfigs.forEach((config) => {
        payload[config.key] = this.selectedValues(config.key);
      });

      this.$emit('filters-change', payload, { trigger });
      return payload;
    },
    emitApply() {
      this.$emit('search-change', this.search);
      const filters = this.emitFilters('apply');
      this.$emit('apply', buildToolbarApplyPayload(this.search, filters));
    },
  },
};
</script>

<style scoped>
.expenses-toolbar-provider-inline {
  --app-filter-inline-width: 190px;
}

.sales-toolbar-search-btn {
  background-color: #0070cd;
  border-color: #0070cd;
  color: #ffffff;
}

.sales-toolbar-search-btn i {
  color: #ffffff;
}

.sales-toolbar-search-btn:hover,
.sales-toolbar-search-btn:focus {
  background-color: #005eae;
  border-color: #005eae;
}

.expenses-filters-grid {
  display: grid;
  gap: 10px;
}

.expenses-filters-grid--first-row,
.expenses-filters-grid--second-row {
  grid-template-columns: repeat(var(--toolbar-grid-columns, 1), minmax(0, 1fr));
}

.expenses-filters-grid--second-row {
  margin-top: 10px;
}

@media (max-width: 1200px) {
  .expenses-filters-grid--first-row,
  .expenses-filters-grid--second-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .expenses-filters-grid--first-row,
  .expenses-filters-grid--second-row {
    grid-template-columns: 1fr;
  }
}
</style>
